import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWallet } from '../contexts/WalletContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Wallet as WalletIcon, 
  Plus, 
  Trash2, 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Calendar,
  Download,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export const Wallet = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { walletData, loading, addIncome, addSpending, removeEntry, refreshData } = useWallet();
  
  // Local state for month/year selection
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Filter wallet data by selected month/year
  const filterByMonth = (items) => {
    return items.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate.getMonth() === selectedMonth && itemDate.getFullYear() === selectedYear;
    });
  };
  
  // Calculate monthly data from filtered wallet data
  const monthlyIncome = filterByMonth(walletData.income || []);
  const monthlySpending = filterByMonth(walletData.spending || []);
  const monthlyData = {
    income: monthlyIncome,
    spending: monthlySpending,
    totalIncome: monthlyIncome.reduce((sum, item) => sum + item.amount, 0),
    totalSpending: monthlySpending.reduce((sum, item) => sum + item.amount, 0)
  };

  const [activeTab, setActiveTab] = useState('overview');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formType, setFormType] = useState('income'); // 'income' or 'spending'
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    category: ''
  });

  // Form submission handler
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.description || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    const entry = {
      ...formData,
      amount: parseFloat(formData.amount)
    };

    if (formType === 'income') {
      addIncome(entry);
      toast.success('Income entry added successfully');
    } else {
      addSpending(entry);
      toast.success('Spending entry added successfully');
    }

    // Reset form
    setFormData({
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      category: ''
    });
    setShowAddForm(false);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const icons = {
      housekeeping: '🏠',
      handyman: '🔧',
    };
    return icons[category] || '💰';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WalletIcon className="w-5 h-5" />
            {t('wallet')} - {t('taxRecords')}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">{t('overview')}</TabsTrigger>
            <TabsTrigger value="income">{t('income')}</TabsTrigger>
            <TabsTrigger value="spending">{t('spending')}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Monthly Balance Card - Data from Calendar */}
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium opacity-90">
                    {t('monthlyBalance')} - {new Date(selectedYear, selectedMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency((monthlyData.totalIncome || 0) - (monthlyData.totalSpending || 0))}
                  </div>
                  <p className="text-xs opacity-75">
                    {(monthlyData.totalIncome - monthlyData.totalSpending) >= 0 ? '📈 Positive' : '📉 Negative'}
                  </p>
                </CardContent>
              </Card>

              {/* Total Income Card - Data from Calendar */}
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium opacity-90 flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    {t('monthlyIncomeFromServices')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(monthlyData.totalIncome || 0)}
                  </div>
                  <p className="text-xs opacity-75">
                    {(monthlyData.income || []).length} {t('services')}
                  </p>
                </CardContent>
              </Card>

              {/* Total Spending Card - Data from Calendar */}
              <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium opacity-90 flex items-center gap-1">
                    <TrendingDown className="w-4 h-4" />
                    {t('monthlySpending')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(monthlyData.totalSpending || 0)}
                  </div>
                  <p className="text-xs opacity-75">
                    {(monthlyData.spending || []).length} {t('expenses')}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Month Navigation & Action Buttons */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    if (selectedMonth === 0) {
                      setSelectedMonth(11);
                      setSelectedYear(selectedYear - 1);
                    } else {
                      setSelectedMonth(selectedMonth - 1);
                    }
                  }}
                >
                  ← {t('previousMonth')}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    if (selectedMonth === 11) {
                      setSelectedMonth(0);
                      setSelectedYear(selectedYear + 1);
                    } else {
                      setSelectedMonth(selectedMonth + 1);
                    }
                  }}
                >
                  {t('nextMonth')} →
                </Button>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={refreshData} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  {t('refresh')}
                </Button>
                <Button variant="outline" onClick={() => {
                  onOpenChange(false); // Close the modal
                  navigate('/tax-report'); // Navigate to tax report
                }}>
                  <Download className="w-4 h-4 mr-2" />
                  {t('exportTax')}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Income Tab */}
          <TabsContent value="income" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">{t('incomeRecords')}</h3>
              <Button onClick={() => { setFormType('income'); setShowAddForm(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                {t('addIncome')}
              </Button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {/* Real income from calendar appointments */}
              {(monthlyData.income || []).map((entry) => (
                <Card key={entry.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getCategoryIcon(entry.category)}</span>
                      <div>
                        <p className="font-medium">{entry.description}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-3 h-3" />
                          {entry.date}
                          <Badge variant="secondary" className="text-xs">
                            {entry.category}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-green-600">
                        +{formatCurrency(entry.amount)}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeEntry(entry.id, 'income')}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Spending Tab */}
          <TabsContent value="spending" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">{t('spendingRecords')}</h3>
              <Button onClick={() => { setFormType('spending'); setShowAddForm(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                {t('addSpending')}
              </Button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {/* Real spending from calendar appointments */}
              {(monthlyData.spending || []).map((entry) => (
                <Card key={entry.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getCategoryIcon(entry.category)}</span>
                      <div>
                        <p className="font-medium">{entry.description}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-3 h-3" />
                          {entry.date}
                          <Badge variant="secondary" className="text-xs">
                            {entry.category}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-red-600">
                        -{formatCurrency(entry.amount)}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeEntry(entry.id, 'spending')}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Add Entry Form Modal */}
        {showAddForm && (
          <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {formType === 'income' ? t('addIncome') : t('addSpending')}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="description">{t('description')}</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={formType === 'income' ? 'House cleaning service' : 'Transportation costs'}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">{t('amount')} (€)</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">{t('date')}</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">{t('category')}</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      {formType === 'income' ? (
                        <>
                          <SelectItem value="housekeeping">🏠 Housekeeping</SelectItem>
                          <SelectItem value="handyman">🔧 Handyman</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="fees">💳 Platform Fees</SelectItem>
                          <SelectItem value="transport">🚗 Transportation</SelectItem>
                          <SelectItem value="supplies">📦 Supplies</SelectItem>
                          <SelectItem value="marketing">📢 Marketing</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                    {t('cancel')}
                  </Button>
                  <Button type="submit">
                    {t('add')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};